/**
 * Socket.io Connection Utility — DoubtQ Student Chat
 * Manages socket connection using the student auth token from localStorage.
 */

import { io } from 'socket.io-client';

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL || 'https://api.doubtq.com';

let socket = null;

/**
 * Initialize socket connection with student JWT token.
 * If a socket already exists and is connected, it is returned as-is.
 * If disconnected, it is reconnected.
 * @param {string} token - JWT auth token from localStorage
 * @returns {Socket} socket instance
 */
export const initSocket = (token) => {
  // Reuse existing connected socket
  if (socket && socket.connected) {
    return socket;
  }

  // Disconnect stale / errored socket before creating a new one
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(SOCKET_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  });

  socket.on('connect', () => {
    console.log('[Chat] Socket connected:', socket.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('[Chat] Socket disconnected:', reason);
  });

  socket.on('connect_error', (err) => {
    console.error('[Chat] Socket connection error:', err.message);
  });

  socket.on('error', (err) => {
    console.error('[Chat] Socket server error:', err?.message || err);
  });

  return socket;
};

/**
 * Get the current socket instance
 * @returns {Socket|null}
 */
export const getSocket = () => socket;

/**
 * Disconnect the socket and clear the instance
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
};
